import { useEffect, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Loader,
  NavLink,
  Tooltip,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
} from '@mantine/core';
import {
  IconFile,
  IconFileTypePdf,
  IconMinus,
  IconPhoto,
  IconPlus,
  IconRotateRectangle,
} from '@tabler/icons-react';
import { useFileViewUrl } from '../hooks/useFileViewUrl.ts';
import type { AccountDocumentFile } from '../model.ts';
import { VIEW_URL_FALLBACK_MESSAGE } from '../utils/apiError.ts';

type DocumentFilePreviewProps = {
  files: AccountDocumentFile[];
};

const FILE_LIST_WIDTH = 200;
const ZOOM_STEP = 25;
const ZOOM_MIN = 25;
const ZOOM_MAX = 400;
const DEFAULT_ZOOM = 100;
const ROTATION_STEP = 90;

const SCAN_STATE_MESSAGE: Record<
  Exclude<AccountDocumentFile['scanState'], 'CLEAN'>,
  string
> = {
  PENDING: '檔案處理中，尚無法預覽',
  INFECTED: '檔案未通過防毒掃描，無法預覽',
  ERROR: '檔案掃描失敗，無法預覽',
};

const isImage = (file: AccountDocumentFile) =>
  file.contentType.startsWith('image/');

const isPdf = (file: AccountDocumentFile) =>
  file.contentType === 'application/pdf';

const FileTypeIcon = ({ file }: { file: AccountDocumentFile }) => {
  if (isImage(file)) {
    return (
      <ThemeIcon variant="light" color="gray" size="md" radius="sm">
        <IconPhoto size={16} />
      </ThemeIcon>
    );
  }

  if (isPdf(file)) {
    return (
      <ThemeIcon variant="light" color="gray" size="md" radius="sm">
        <IconFileTypePdf size={16} />
      </ThemeIcon>
    );
  }

  return (
    <ThemeIcon variant="light" color="gray" size="md" radius="sm">
      <IconFile size={16} />
    </ThemeIcon>
  );
};

const FileTags = ({ tags }: { tags: string[] }) => (
  <Group gap={4} wrap="wrap">
    {tags.map((tag) => (
      <Badge key={tag} size="xs" variant="filled" radius="sm">
        {tag}
      </Badge>
    ))}
  </Group>
);

type FileViewerProps = {
  file: AccountDocumentFile;
  viewUrl: string | undefined;
  isLoading: boolean;
  errorMessage: string | undefined;
  onRetry: () => void;
  zoom: number;
  rotation: number;
};

const FileViewer = ({
  file,
  viewUrl,
  isLoading,
  errorMessage,
  onRetry,
  zoom,
  rotation,
}: FileViewerProps) => {
  if (isLoading) {
    return (
      <Center h="100%">
        <Loader />
      </Center>
    );
  }

  const unviewableMessage =
    file.scanState === 'CLEAN' ? undefined : SCAN_STATE_MESSAGE[file.scanState];

  if (unviewableMessage) {
    return (
      <Center h="100%">
        <Text c="dimmed">{unviewableMessage}</Text>
      </Center>
    );
  }

  if (!viewUrl) {
    return (
      <Center h="100%">
        <Stack align="center" gap="sm">
          <Text c="dimmed" size="sm">
            {errorMessage ?? VIEW_URL_FALLBACK_MESSAGE}
          </Text>
          <Button variant="default" size="xs" onClick={onRetry}>
            重新載入
          </Button>
        </Stack>
      </Center>
    );
  }

  if (isImage(file)) {
    return (
      <ScrollArea h="100%" type="auto">
        <Box display="flex" mih="100%" p="xl">
          <img
            src={viewUrl}
            alt={file.fileName}
            style={{
              width: `${zoom}%`,
              flexShrink: 0,
              margin: 'auto',
              maxWidth: 'none',
              objectFit: 'contain',
              boxShadow: 'var(--mantine-shadow-sm)',
              backgroundColor: 'var(--mantine-color-white)',
              transform: `rotate(${rotation}deg)`,
              transformOrigin: 'center center',
            }}
          />
        </Box>
      </ScrollArea>
    );
  }

  return (
    <iframe
      key={zoom}
      src={`${viewUrl}#zoom=${zoom}`}
      title={file.fileName}
      style={{ width: '100%', height: '100%', border: 'none' }}
    />
  );
};

const DocumentFilePreview = ({ files }: DocumentFilePreviewProps) => {
  const sortedFiles = [...files].sort((a, b) => a.pageOrder - b.pageOrder);
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [rotation, setRotation] = useState(0);

  const activeFile = sortedFiles[activeIndex];
  const { viewUrl, isLoading, errorMessage, retry } =
    useFileViewUrl(activeFile);

  useEffect(() => {
    setZoom(DEFAULT_ZOOM);
    setRotation(0);
  }, [activeFile?.id]);

  if (!activeFile) {
    return (
      <Center h="100%">
        <Text c="dimmed">此文件沒有任何檔案</Text>
      </Center>
    );
  }

  const resetView = () => {
    setZoom(DEFAULT_ZOOM);
    setRotation(0);
  };

  return (
    <Group align="stretch" h="100%" gap={0} wrap="nowrap">
      <Stack w={FILE_LIST_WIDTH} gap="xs" style={{ flexShrink: 0 }} pr="md">
        <Text size="xs" c="dimmed">
          檔案清單 · {sortedFiles.length}
        </Text>

        <ScrollArea style={{ flex: 1 }}>
          <Stack gap={4}>
            {sortedFiles.map((file, index) => (
              <NavLink
                key={file.id}
                active={index === activeIndex}
                variant="light"
                onClick={() => setActiveIndex(index)}
                leftSection={<FileTypeIcon file={file} />}
                label={
                  <Stack gap={4}>
                    <Text size="sm" fw={500} truncate="end">
                      {file.fileName}
                    </Text>
                    <FileTags tags={file.tags} />
                  </Stack>
                }
              />
            ))}
          </Stack>
        </ScrollArea>
      </Stack>

      <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
        <Group
          bg="dark.7"
          px="md"
          py="xs"
          justify="space-between"
          wrap="nowrap"
        >
          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} c="white" truncate="end">
              {activeFile.fileName}
            </Text>
            <FileTags tags={activeFile.tags} />
          </Group>

          {!isPdf(activeFile) && (
            <>
              <Group gap="xs" wrap="nowrap">
                <Tooltip label="順時針旋轉">
                  <ActionIcon
                    variant="subtle"
                    color="gray.3"
                    size="sm"
                    aria-label="順時針旋轉"
                    onClick={() =>
                      setRotation((value) => (value + ROTATION_STEP) % 360)
                    }
                  >
                    <IconRotateRectangle size={14} />
                  </ActionIcon>
                </Tooltip>

                <Group gap={4} bg="dark.5" px={4} py={2} wrap="nowrap">
                  <ActionIcon
                    variant="subtle"
                    color="gray.3"
                    size="sm"
                    aria-label="縮小"
                    disabled={zoom <= ZOOM_MIN}
                    onClick={() =>
                      setZoom((value) => Math.max(ZOOM_MIN, value - ZOOM_STEP))
                    }
                  >
                    <IconMinus size={14} />
                  </ActionIcon>
                  <Text size="xs" c="white" w={40} ta="center">
                    {zoom}%
                  </Text>
                  <ActionIcon
                    variant="subtle"
                    color="gray.3"
                    size="sm"
                    aria-label="放大"
                    disabled={zoom >= ZOOM_MAX}
                    onClick={() =>
                      setZoom((value) => Math.min(ZOOM_MAX, value + ZOOM_STEP))
                    }
                  >
                    <IconPlus size={14} />
                  </ActionIcon>
                </Group>

                <Button
                  variant="outline"
                  color="gray.3"
                  size="xs"
                  onClick={resetView}
                >
                  重設
                </Button>
              </Group>
            </>
          )}
        </Group>

        <Box
          bg="gray.1"
          style={{ flex: 1, minHeight: 0, position: 'relative' }}
        >
          <FileViewer
            file={activeFile}
            viewUrl={viewUrl}
            isLoading={isLoading}
            errorMessage={errorMessage}
            onRetry={retry}
            zoom={zoom}
            rotation={rotation}
          />
        </Box>
      </Stack>
    </Group>
  );
};

export { DocumentFilePreview };
